import { useEffect, useState } from 'react';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';

// TENANTS-DELETE-V1: pc kept as fallback colors; PLANS now fetched from API
const pc = { BASIC:'#4a6580', STARTER:'#1a5cff', PRO:'#7c3aed', BUSINESS:'#059669', ENTERPRISE:'#d97706', FREE:'#4a6580' };

export default function Tenants() {
  const { role } = useAuth();
  // TENANTS-DELETE-V1: dynamic plans from API
  const [PLANS, setPlans] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [modal, setModal] = useState(null);
  const [newPlan, setNewPlan] = useState('');
  const [reason, setReason] = useState('');
  const [overrideLimit, setOverrideLimit] = useState('');
  const [msg, setMsg] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Cancel flow state
  const [cancelStep, setCancelStep] = useState('confirm'); // 'confirm' | 'otp'
  const [cancelOtp, setCancelOtp] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [otpRequesting, setOtpRequesting] = useState(false);

  // TENANTS-DELETE-V1: delete flow state (mirrors cancel pattern)
  const [deleteStep, setDeleteStep] = useState('confirm');
  const [deleteOtp, setDeleteOtp] = useState('');

  const inp = { padding:'10px 14px', background:'#f9f9f9', border:'1px solid #ddd', borderRadius:8, color:'#000', fontSize:14, outline:'none' };
  const btn = (c, tc) => ({ padding:'7px 14px', background:c, color:'#000', border:'none', borderRadius:6, cursor:'pointer', fontSize:13, fontWeight:600 });

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams({ size:100 });
    if(planFilter) p.append('plan',planFilter);
    client.get('/admin/tenants?'+p).then(r=>setTenants(r.data.tenants||[])).finally(()=>setLoading(false));
  };

  useEffect(()=>{ load(); }, [planFilter]);

  // TENANTS-DELETE-V1: fetch plans from API once on mount
  useEffect(() => {
    client.get('/admin/plans').then(r => {
      const list = (r.data || [])
        .filter(p => p.is_active !== false)
        .sort((a, b) => (a.display_order ?? 99) - (b.display_order ?? 99))
        .map(p => p.name);
      setPlans(list.length ? list : ['BASIC','STARTER','PRO','BUSINESS','ENTERPRISE']);
    }).catch(() => {
      setPlans(['BASIC','STARTER','PRO','BUSINESS','ENTERPRISE']);
    });
  }, []);

  // TENANTS-DELETE-V1: open delete modal (resets to confirm step)
  const openDeleteModal = (t) => {
    setSelected(t);
    setDeleteStep('confirm');
    setDeleteOtp('');
    setMsg('');
    setModal('delete');
  };

  // TENANTS-DELETE-V1: request OTP for delete
  const requestDeleteOtp = async () => {
    setOtpRequesting(true);
    try {
      await client.post('/admin/tenants/'+selected.id+'/delete/request-otp');
      setDeleteStep('otp');
      setMsg('OTP sent to your admin email');
    } catch(e) {
      setMsg(e.response?.data?.error || e.response?.data || 'Failed to send OTP');
    } finally {
      setOtpRequesting(false);
    }
  };

  // TENANTS-DELETE-V1: confirm permanent deletion
  const confirmDelete = async () => {
    if (!deleteOtp) return setMsg('OTP is required');
    setActionLoading(true);
    try {
      await client.delete('/admin/tenants/'+selected.id, { data: { otp: deleteOtp } });
      setMsg('Tenant ' + selected.name + ' permanently deleted');
      setModal(null);
      load();
    } catch(e) {
      setMsg(e.response?.data?.error || e.response?.data || 'Delete failed');
    } finally {
      setActionLoading(false);
    }
  };

  const filtered = tenants.filter(t => !search || t.name?.toLowerCase().includes(search.toLowerCase()) || t.email?.toLowerCase().includes(search.toLowerCase()));

  const openCancelModal = (t) => {
    setSelected(t);
    setCancelStep('confirm');
    setCancelOtp('');
    setCancelReason('');
    setMsg('');
    setModal('cancel');
  };

  const reinstateCancellation = async (t) => {
    if (!window.confirm('Reverse the scheduled cancellation for ' + t.name + '?\n\nThe subscription will continue billing normally. Tenant and admin team will be notified.')) return;
    try {
      await client.post('/admin/tenants/' + t.id + '/reinstate-cancellation');
      setMsg('Cancellation reversed. ' + t.name + ' will continue normally.');
      load();
    } catch(e) {
      setMsg(e.response?.data?.error || e.response?.data || 'Reinstate failed');
    }
  };


  const requestCancelOtp = async () => {
    setOtpRequesting(true);
    try {
      await client.post('/admin/tenants/'+selected.id+'/cancel/request-otp');
      setCancelStep('otp');
      setMsg('OTP sent to your admin email');
    } catch(e) {
      setMsg(e.response?.data?.error || e.response?.data || 'Failed to send OTP');
    } finally {
      setOtpRequesting(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelOtp) return setMsg('OTP is required');
    setActionLoading(true);
    try {
      await client.post('/admin/tenants/'+selected.id+'/cancel', {
        otp: cancelOtp,
        reason: cancelReason || 'Canceled via admin panel'
      });
      setMsg('Subscription scheduled to cancel at end of billing period');
      setModal(null);
      load();
    } catch(e) {
      setMsg(e.response?.data?.error || e.response?.data || 'Cancel failed');
    } finally {
      setActionLoading(false);
    }
  };

  const doAction = async () => {
    setActionLoading(true);
    try {
      if(modal==='plan') await client.post('/admin/tenants/'+selected.id+'/plan', { plan:newPlan });
      else if(modal==='suspend') await client.post('/admin/tenants/'+selected.id+'/suspend', { reason });
      else if(modal==='override') await client.post('/admin/tenants/'+selected.id+'/override-limit', { limit:parseInt(overrideLimit) });
      setMsg('Done'); setModal(null); load();
    } catch(e) { setMsg(e.response?.data||'Failed'); } finally { setActionLoading(false); }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <h1 style={{ color:'#000', fontSize:22, fontWeight:700, margin:0, fontFamily:'Georgia,serif' }}>Tenants</h1>
        <div style={{ color:'#555', fontSize:14 }}>{filtered.length} tenants</div>
      </div>
      <div style={{ display:'flex', gap:12, marginBottom:20 }}>
        <input style={{ ...inp, width:260 }} placeholder="Search name or email..." value={search} onChange={e=>setSearch(e.target.value)} />
        <select style={inp} value={planFilter} onChange={e=>setPlanFilter(e.target.value)}>
          <option value="">All Plans</option>
          {PLANS.map(p=><option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      {msg && <div style={{ background:'#f0fff6', border:'1px solid #b2dfdb', borderRadius:8, padding:'10px 16px', color:'#0a8a40', fontSize:13, marginBottom:16 }}>{msg} <button onClick={()=>setMsg('')} style={{ background:'none', border:'none', color:'#0a8a40', cursor:'pointer' }}>✕</button></div>}
      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #eee', overflow:'hidden' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ borderBottom:'1px solid #eee' }}>{['Tenant','Plan','Docs Total','Docs This Mo','Invoices Total','Invoices This Mo','Status','Actions'].map(h=><th key={h} style={{ color:'#555', fontSize:11, fontWeight:500, textAlign:'left', padding:'14px 16px', textTransform:'uppercase' }}>{h}</th>)}</tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} style={{ color:'#555', padding:24, textAlign:'center' }}>Loading...</td></tr>
            : filtered.map(t=>(
              <tr key={t.id} style={{ borderBottom:'1px solid #f5f5f5' }}>
                <td style={{ padding:'12px 16px' }}><div style={{ color:'#000', fontSize:14, fontWeight:500 }}>{t.name}</div><div style={{ color:'#555', fontSize:12 }}>{t.email}</div></td>
                <td style={{ padding:'12px 16px' }}><span style={{ background:(pc[t.plan]||'#4a6580')+'22', color:pc[t.plan]||'#4a6580', padding:'3px 8px', borderRadius:4, fontSize:12, fontWeight:700 }}>{t.plan}</span></td>
                <td style={{ padding:'12px 16px', color:'#333', fontSize:14 }}>{t.documents_used_total??0}</td>
                <td style={{ padding:'12px 16px', color:'#333', fontSize:14 }}>{t.docs_this_month??0}</td>
                {/* TENANTS-INVOICES-V1: invoice count cells, same styling as docs */}
                <td style={{ padding:'12px 16px', color:'#333', fontSize:14, background:'#fafbff' }}>{t.total_invoices??0}</td>
                <td style={{ padding:'12px 16px', color:'#333', fontSize:14, background:'#fafbff' }}>{t.invoices_this_month??0}</td>
                <td style={{ padding:'12px 16px' }}>
                  {t.cancel_at_period_end ? (
                    <span style={{ background:'#fff8ec', color:'#b45309', padding:'3px 8px', borderRadius:4, fontSize:12, fontWeight:600 }}>CANCELING</span>
                  ) : (
                    <span style={{ background:t.suspended_at?'#fff5f5':'#f0fff6', color:t.suspended_at?'#c0392b':'#0a8a40', padding:'3px 8px', borderRadius:4, fontSize:12, fontWeight:600 }}>{t.suspended_at?'SUSPENDED':'ACTIVE'}</span>
                  )}
                </td>
                <td style={{ padding:'12px 16px' }}>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    <button style={btn('#eff4ff','#1a3cff')} onClick={()=>{ setSelected(t); setNewPlan(t.plan); setModal('plan'); }}>Plan</button>
                    {t.plan !== 'FREE' && !t.cancel_at_period_end && (
                      <button style={btn('#fff5f5','#c0392b')} onClick={()=>openCancelModal(t)}>Cancel</button>
                    )}
                    {t.cancel_at_period_end && (
                      <button style={btn('#f0fff6','#0a8a40')} onClick={()=>reinstateCancellation(t)} title={t.cancel_effective_date ? 'Scheduled to cancel on ' + new Date(t.cancel_effective_date).toLocaleDateString() : 'Reinstate subscription'}>Reinstate</button>
                    )}
                    {t.suspended_at ? <button style={btn('#f0fff6','#0a8a40')} onClick={async(e)=>{ e.target.disabled=true; e.target.textContent='...'; await client.post('/admin/tenants/'+t.id+'/reactivate'); load(); }}>Reactivate</button>
                    : <button style={btn('#fff8ec','#b45309')} onClick={()=>{ setSelected(t); setModal('suspend'); }}>Suspend</button>}
                    {role==='SUPER_ADMIN' && <button style={btn('#fef2f2','#991b1b')} onClick={()=>openDeleteModal(t)} title="Permanently delete this tenant and all their data">Delete</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Plan / Suspend / Override modal (unchanged) */}
      {modal && modal !== 'cancel' && modal !== 'delete' && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:32, width:380, border:'1px solid #eee' }}>
            <h3 style={{ color:'#000', margin:'0 0 16px', fontFamily:'Georgia,serif' }}>{modal==='plan'?'Change Plan':modal==='suspend'?'Suspend Tenant':'Override Limit'}</h3>
            <div style={{ color:'#555', fontSize:13, marginBottom:16 }}>{selected?.name}</div>
            {modal==='plan' && <select style={{ ...inp, width:'100%', marginBottom:16 }} value={newPlan} onChange={e=>setNewPlan(e.target.value)}>{PLANS.map(p=><option key={p} value={p}>{p}</option>)}</select>}
            {modal==='suspend' && <input style={{ ...inp, width:'100%', marginBottom:16, boxSizing:'border-box' }} placeholder="Reason" value={reason} onChange={e=>setReason(e.target.value)} />}
            {modal==='override' && <input style={{ ...inp, width:'100%', marginBottom:16, boxSizing:'border-box' }} type="number" placeholder="-1 = unlimited" value={overrideLimit} onChange={e=>setOverrideLimit(e.target.value)} />}
            <div style={{ display:'flex', gap:10 }}>
              <button style={{ ...btn('#eff4ff','#1a3cff'), flex:1, background:'#1a3cff', color:'#fff', padding:'11px', borderRadius:8 }} onClick={doAction}>Confirm</button>
              <button style={{ flex:1, padding:'11px', background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700 }} onClick={()=>setModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel subscription modal */}
      {modal === 'cancel' && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:32, width:460, border:'1px solid #eee' }}>
            <h3 style={{ color:'#c0392b', margin:'0 0 6px', fontFamily:'Georgia,serif' }}>Cancel Subscription</h3>
            <div style={{ color:'#555', fontSize:13, marginBottom:20 }}>{selected?.name} — {selected?.email}</div>

            {cancelStep === 'confirm' && (
              <>
                <div style={{ background:'#fff8ec', border:'1px solid #fcd34d', borderRadius:8, padding:14, marginBottom:16, color:'#92400e', fontSize:13, lineHeight:1.6 }}>
                  <strong>What happens when you cancel:</strong>
                  <ul style={{ margin:'8px 0 0', paddingLeft:20 }}>
                    <li>Tenant keeps full access until end of current billing period</li>
                    <li>No refund will be issued</li>
                    <li>Subscription stops at next renewal date</li>
                    <li>After period end: account moves to read-only mode (FREE plan)</li>
                    <li>All signed contracts remain available forever</li>
                  </ul>
                </div>
                <label style={{ color:'#333', fontSize:12, fontWeight:700, display:'block', marginBottom:6, textTransform:'uppercase' }}>Reason (internal)</label>
                <input style={{ ...inp, width:'100%', marginBottom:20, boxSizing:'border-box' }}
                  placeholder="e.g. Customer requested via support ticket #1234"
                  value={cancelReason}
                  onChange={e=>setCancelReason(e.target.value)} />
                <div style={{ display:'flex', gap:10 }}>
                  <button style={{ flex:1, padding:'11px', background:'#c0392b', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:otpRequesting?'not-allowed':'pointer', opacity:otpRequesting?0.6:1 }}
                    onClick={requestCancelOtp} disabled={otpRequesting}>
                    {otpRequesting ? 'Sending OTP...' : 'Send OTP to My Email'}
                  </button>
                  <button style={{ flex:1, padding:'11px', background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}
                    onClick={()=>setModal(null)}>Close</button>
                </div>
              </>
            )}

            {cancelStep === 'otp' && (
              <>
                <div style={{ background:'#f0fff6', border:'1px solid #b2dfdb', borderRadius:8, padding:'10px 14px', color:'#0a8a40', fontSize:13, marginBottom:16 }}>
                  ✓ OTP sent to your admin email — check your inbox
                </div>
                <label style={{ color:'#333', fontSize:12, fontWeight:700, display:'block', marginBottom:6, textTransform:'uppercase' }}>Enter OTP</label>
                <input style={{ ...inp, width:'100%', marginBottom:20, boxSizing:'border-box', fontSize:18, letterSpacing:4, textAlign:'center' }}
                  placeholder="6-digit code"
                  value={cancelOtp}
                  onChange={e=>setCancelOtp(e.target.value)}
                  maxLength={6}
                  autoFocus />
                <div style={{ display:'flex', gap:10 }}>
                  <button style={{ flex:1, padding:'11px', background:'#c0392b', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:(!cancelOtp||actionLoading)?'not-allowed':'pointer', opacity:(!cancelOtp||actionLoading)?0.6:1 }}
                    onClick={confirmCancel} disabled={!cancelOtp || actionLoading}>
                    {actionLoading ? 'Canceling...' : 'Confirm Cancellation'}
                  </button>
                  <button style={{ flex:1, padding:'11px', background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}
                    onClick={()=>setModal(null)}>Cancel</button>
                </div>
                <div style={{ marginTop:12, textAlign:'center' }}>
                  <button onClick={requestCancelOtp} style={{ background:'none', border:'none', color:'#1a3cff', fontSize:12, cursor:'pointer' }}>Resend OTP</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* TENANTS-DELETE-V1: Permanent delete modal (OTP-gated, SUPER_ADMIN only) */}
      {modal === 'delete' && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:32, width:480, border:'1px solid #eee' }}>
            <h3 style={{ color:'#991b1b', margin:'0 0 6px', fontFamily:'Georgia,serif' }}>Permanently Delete Tenant</h3>
            <div style={{ color:'#555', fontSize:13, marginBottom:20 }}>{selected?.name} — {selected?.email}</div>

            {deleteStep === 'confirm' && (
              <>
                <div style={{ background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:8, padding:14, marginBottom:16, color:'#7f1d1d', fontSize:13, lineHeight:1.6 }}>
                  <strong>This action is IRREVERSIBLE.</strong>
                  <ul style={{ margin:'8px 0 0', paddingLeft:20 }}>
                    <li>Stripe subscription will be canceled immediately (no refund)</li>
                    <li>All documents, signers, templates, and signatures will be deleted</li>
                    <li>All invoices, customers, and payment history will be deleted</li>
                    <li>All team members and API keys will be deleted</li>
                    <li>Affiliate commissions linked to this tenant will be removed</li>
                    <li>Audit log entry will be created with full deletion details</li>
                    <li>This cannot be undone. Make sure you have a backup if needed.</li>
                  </ul>
                </div>
                <div style={{ background:'#fff8ec', border:'1px solid #fcd34d', borderRadius:8, padding:'10px 14px', marginBottom:16, color:'#92400e', fontSize:12 }}>
                  Note: ENTERPRISE tenants cannot be deleted from this UI — downgrade their plan first.
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  <button style={{ flex:1, padding:'11px', background:'#991b1b', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:otpRequesting?'not-allowed':'pointer', opacity:otpRequesting?0.6:1 }}
                    onClick={requestDeleteOtp} disabled={otpRequesting}>
                    {otpRequesting ? 'Sending OTP...' : 'Send OTP to My Email'}
                  </button>
                  <button style={{ flex:1, padding:'11px', background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}
                    onClick={()=>setModal(null)}>Cancel</button>
                </div>
              </>
            )}

            {deleteStep === 'otp' && (
              <>
                <div style={{ background:'#f0fff6', border:'1px solid #b2dfdb', borderRadius:8, padding:'10px 14px', color:'#0a8a40', fontSize:13, marginBottom:16 }}>
                  &#10003; OTP sent to your admin email &mdash; check your inbox
                </div>
                <label style={{ color:'#333', fontSize:12, fontWeight:700, display:'block', marginBottom:6, textTransform:'uppercase' }}>Enter OTP to Confirm Deletion</label>
                <input style={{ ...inp, width:'100%', marginBottom:20, boxSizing:'border-box', fontSize:18, letterSpacing:4, textAlign:'center' }}
                  placeholder="6-digit code"
                  value={deleteOtp}
                  onChange={e=>setDeleteOtp(e.target.value)}
                  maxLength={6}
                  autoFocus />
                <div style={{ display:'flex', gap:10 }}>
                  <button style={{ flex:1, padding:'11px', background:'#991b1b', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:(!deleteOtp||actionLoading)?'not-allowed':'pointer', opacity:(!deleteOtp||actionLoading)?0.6:1 }}
                    onClick={confirmDelete} disabled={!deleteOtp || actionLoading}>
                    {actionLoading ? 'Deleting...' : 'Permanently Delete'}
                  </button>
                  <button style={{ flex:1, padding:'11px', background:'#fff', color:'#555', border:'1px solid #ddd', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}
                    onClick={()=>setModal(null)}>Cancel</button>
                </div>
                <div style={{ marginTop:12, textAlign:'center' }}>
                  <button onClick={requestDeleteOtp} style={{ background:'none', border:'none', color:'#1a3cff', fontSize:12, cursor:'pointer' }}>Resend OTP</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
